/**
 * MarkdownContent - Renders markdown content with proper formatting
 *
 * Uses react-markdown with remark-gfm for GitHub Flavored Markdown support.
 * Styled to match the report-content CSS classes.
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';

interface MarkdownContentProps {
  content: string;
  className?: string;
  /** When true, renders inline without block-level margins for use in flex containers */
  inline?: boolean;
}

export function MarkdownContent({ content, className, inline = false }: MarkdownContentProps) {
  // Check if className includes "inline" to auto-detect inline mode
  const isInlineMode = inline || className?.includes('inline');

  return (
    <div className={cn(
      'report-content prose prose-invert prose-sm max-w-none',
      // When inline, remove prose margins and make content flow inline
      isInlineMode && '[&_p]:mb-0 [&_p]:inline [&_ul]:mb-0 [&_ol]:mb-0 [&_ul]:mt-1 [&_ol]:mt-1',
      className
    )}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Style headings
          h1: ({ children }: { children?: React.ReactNode }) => (
            <h1 className={cn("text-xl font-bold text-text-primary", isInlineMode ? "mb-1 mt-1" : "mb-4 mt-6")}>{children}</h1>
          ),
          h2: ({ children }: { children?: React.ReactNode }) => (
            <h2 className={cn("text-lg font-semibold text-text-primary", isInlineMode ? "mb-1 mt-1" : "mb-3 mt-5")}>{children}</h2>
          ),
          h3: ({ children }: { children?: React.ReactNode }) => (
            <h3 className={cn("text-base font-semibold text-text-primary", isInlineMode ? "mb-1 mt-1" : "mb-2 mt-4")}>{children}</h3>
          ),
          // Style paragraphs - no margin when inline
          p: ({ children }: { children?: React.ReactNode }) => (
            <p className={cn("text-text-primary text-sm leading-relaxed", isInlineMode ? "mb-0" : "mb-3")}>{children}</p>
          ),
          // Style lists - use list-outside with proper padding for correct alignment
          ul: ({ children }: { children?: React.ReactNode }) => (
            <ul className={cn("list-disc list-outside pl-5 space-y-1 text-text-primary text-sm", isInlineMode ? "mb-0 mt-1" : "mb-3")}>{children}</ul>
          ),
          ol: ({ children }: { children?: React.ReactNode }) => (
            <ol className={cn("list-decimal list-outside pl-5 space-y-1 text-text-primary text-sm", isInlineMode ? "mb-0 mt-1" : "mb-3")}>{children}</ol>
          ),
          li: ({ children }: { children?: React.ReactNode }) => (
            <li className="text-text-primary pl-1">{children}</li>
          ),
          // Style emphasis
          strong: ({ children }: { children?: React.ReactNode }) => (
            <strong className="font-semibold text-text-primary">{children}</strong>
          ),
          em: ({ children }: { children?: React.ReactNode }) => (
            <em className="italic text-text-primary">{children}</em>
          ),
          // Style code
          code: ({ children, className: codeClassName }: { children?: React.ReactNode; className?: string }) => {
            const isInline = !codeClassName;
            return isInline ? (
              <code className="bg-surface-light/50 px-1.5 py-0.5 rounded text-xs font-mono text-verdict-primary">
                {children}
              </code>
            ) : (
              <code className="block bg-surface/50 p-3 rounded-lg text-xs font-mono text-text-primary overflow-x-auto">
                {children}
              </code>
            );
          },
          pre: ({ children }: { children?: React.ReactNode }) => (
            <pre className="bg-surface/50 p-4 rounded-lg overflow-x-auto mb-4">
              {children}
            </pre>
          ),
          // Style blockquotes
          blockquote: ({ children }: { children?: React.ReactNode }) => (
            <blockquote className="border-l-2 border-verdict-primary/50 pl-4 italic text-text-secondary my-4">
              {children}
            </blockquote>
          ),
          // Style links
          a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-verdict-primary hover:text-verdict-primary-hover underline underline-offset-2"
            >
              {children}
            </a>
          ),
          // Style tables
          table: ({ children }: { children?: React.ReactNode }) => (
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border-collapse border border-border">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }: { children?: React.ReactNode }) => (
            <thead className="bg-surface-light">{children}</thead>
          ),
          tbody: ({ children }: { children?: React.ReactNode }) => (
            <tbody className="divide-y divide-zinc-700">{children}</tbody>
          ),
          tr: ({ children }: { children?: React.ReactNode }) => (
            <tr className="hover:bg-surface-light/50">{children}</tr>
          ),
          th: ({ children }: { children?: React.ReactNode }) => (
            <th className="px-3 py-2 text-left text-xs font-semibold text-text-primary border border-border">
              {children}
            </th>
          ),
          td: ({ children }: { children?: React.ReactNode }) => (
            <td className="px-3 py-2 text-sm text-text-primary border border-border">
              {children}
            </td>
          ),
          // Style horizontal rules
          hr: () => (
            <hr className="border-border my-6" />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

